import * as React from 'react';
import { CompanyDetailedDto, TriggerScoringByNameDto } from 'models/dtoTypes';
import { styled } from '@mui/material/styles';
import { Score, PollCompanyScore, GetCompany } from 'services/openapiClient';
import RawIconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import BlockIcon from '@mui/icons-material/Block';
import DownloadIcon from '@mui/icons-material/Download';
// import { dateAdd } from 'utils/date';
// import UpdateIcon from '@mui/icons-material/Update';

const IconButton = styled(RawIconButton)({
  fontSize: 50,
});

interface OnComplete {
  (company: CompanyDetailedDto): void;
}

interface OnOutOfCredits {
  (): void;
}

interface OnTrigger {
  (id: string): void;
}

interface Props {
  // older than 90 days, will show trigger button ignore the score
  lastUpdate?: string | null;
  company: CompanyDetailedDto;
  initialState: string;
  initialScore: number;
  onOutOfCredits?: OnOutOfCredits;
  disabled?: boolean; // cannot request update
  onComplete?: OnComplete;
  onTrigger?: OnTrigger;
  onStarted: () => void;
  onRollback: () => void;
}

export default function KosmosScoreUpdateWidget(props: Props): JSX.Element {
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const POLL_INTERVAL = 5000; // poll company result every 5 seconds

  const {
    initialState,
    initialScore,
    company,
    onComplete,
    onTrigger,
    onStarted,
    onRollback,
    onOutOfCredits,
    disabled,
  } = props;
  const [companyId, setCompanyId] = React.useState<string>(
    company.id || '00000000-0000-0000-0000-000000000000',
  );
  const [scoringState, setScoringState] = React.useState<string>(initialState);

  React.useEffect(() => {
    setScoringState(initialState);
  }, [initialState]);

  React.useEffect(() => {
    return () => {
      // clear setInterval on unmount
      console.log(`stop polling: ${companyId || 'unknown Id'}`);
      stopPoll();
    };
  }, []);

  const startPoll = (scoringCompanyId: string) => {
    const timer = timerRef.current;
    if (timer) {
      clearInterval(timer);
    }
    timerRef.current = setInterval(() => {
      void (async () => {
        await handlePollCompanyScore(scoringCompanyId);
      })();
    }, POLL_INTERVAL);
  };

  const stopPoll = () => {
    const timer = timerRef.current;
    if (timer) {
      console.log('stop polling company', companyId);
      clearInterval(timer);
      timerRef.current = null;
    }
  };

  const handlePollCompanyScore = async (scoringCompanyId: string) => {
    if (scoringCompanyId === '00000000-0000-0000-0000-000000000000') {
      setScoringState('NotStarted');
      stopPoll();
      return;
    }
    try {
      const response = await PollCompanyScore(scoringCompanyId);
      const company = response.data;

      // got the updated company
      switch (company.scoringState) {
        case 'NotStarted':
        case 'InProgress': {
          console.log('updated: ', company.scoringState);
          break;
        }
        case 'Completed': {
          const latestCompany = await GetCompany(scoringCompanyId);
          handleCompleteUpdate(latestCompany?.data || company);
          break;
        }
        default:
          console.error('Unknown state');
      }
    } catch (e) {
      console.error(e);
      const res = await GetCompany(scoringCompanyId);
      if (res?.data) {
        handleError(res.data);
      }
    }
  };

  const handleCompleteUpdate = (company: CompanyDetailedDto) => {
    stopPoll();
    onComplete && onComplete(company);
  };

  const handleError = (newCompany: CompanyDetailedDto) => {
    newCompany.scoringState = 'Error';
    stopPoll();
    onComplete && onComplete(newCompany);
  };

  const manualTriggerDto: TriggerScoringByNameDto = {
    companyName: '',
    countryCode: '',
    registeredAddress: '',
  };

  const handleStartUpdate = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    let scoringCompanyId = companyId || '';
    if (companyId === '00000000-0000-0000-0000-000000000000') {
      scoringCompanyId = '';
      manualTriggerDto.companyName = company.name;
      manualTriggerDto.countryCode = company.countryCode;
      manualTriggerDto.registeredAddress = company.addressInFull;
      manualTriggerDto.additionalInfo = company.additionalInfo;
    }
    onStarted();
    const updated = await Score({
      id: scoringCompanyId,
      body: manualTriggerDto,
    });
    const { data, statusCode, errorCode } = updated;
    if (statusCode === 200) {
      scoringCompanyId = data?.id || '';
      setCompanyId(scoringCompanyId);
      // start polling every 5 seconds
      onTrigger && onTrigger(scoringCompanyId);
      setScoringState('InProgress');
      startPoll(scoringCompanyId);
    } else {
      // trigger failed, rollback
      stopPoll();
      onRollback();
      setScoringState('NotStarted');
      if (statusCode === 402 && errorCode === 'OutOfCredits' && onOutOfCredits) {
        onOutOfCredits();
      }
    }
  };

  const renderTriggerButton = (error = false) => {
    if (disabled) {
      return (
        <IconButton
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
          color="inherit"
          aria-label="block"
          size="large"
        >
          <BlockIcon fontSize="inherit" />
        </IconButton>
      );
    }
    return (
      <IconButton onClick={handleStartUpdate} color="inherit" aria-label="delete" size="large">
        <DownloadIcon fontSize="inherit" color={error ? 'error' : undefined} />
      </IconButton>
    );
  };

  const renderInProgress = () => {
    return (
      <div style={{ marginTop: 15 }}>
        <CircularProgress color="inherit" size={40} />
      </div>
    );
  };

  const renderCompleted = () => {
    return <span style={{ fontSize: 40, lineHeight: 2 }}>{initialScore.toString()}</span>;
  };

  // score lastUpdate older than 90 days ago
  // if (lastUpdate && new Date(lastUpdate) < dateAdd(lastUpdate, -90)) {
  //  return renderTriggerButton();
  // }
  const renderScoreBox = () => {
    switch (scoringState) {
      case 'InProgress':
        return renderInProgress();
      case 'Completed':
        return renderCompleted();
      case 'Error':
        return renderTriggerButton(true);
      case 'NotStarted':
      default:
        return renderTriggerButton();
    }
  };

  return renderScoreBox();
}
